/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * CredentialsManagerDialog
 * -------------------------
 * Full credential management modal for an Application.
 *
 * Features:
 * - Lists all existing OAuth2 credentials with created date.
 * - Per-credential actions: View client_id, Regenerate secret, Delete.
 * - "Add New Credentials" inline form: name + platform type (driven by PLATFORM_OPTIONS).
 * - Newly generated secrets are revealed once via ClientKeySecretDialog.
 * - Themed to match the rest of the Admin UI (gradient border, theme colors).
 *
 * Props:
 *  - open           (boolean)   Controls dialog visibility.
 *  - onClose        (function)  Handler to close the dialog.
 *  - application    (object)    The application object { id, name, oauth2_clients }.
 *  - apiHelper      (object)    Configured APIHelper instance for API calls.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useTheme } from "../../theme/ThemeContext";
import ClientKeySecretDialog from "./ClientKeySecretDialog";
import { useAlertDialog } from "./AlertDialogContext";

const VIEWS = { LIST: "list", CREATE: "create" };

const PLATFORM_OPTIONS = [
    { value: "web",     label: "Web",     icon: "bi-globe"   },
    { value: "mobile",  label: "Mobile",  icon: "bi-phone"   },
];

function getPlatformMeta(type) {
    return (
        PLATFORM_OPTIONS.find(p => p.value === (type || "").toLowerCase()) ||
        { value: type, label: type || "Other", icon: "bi-grid" }
    );
}

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        return new Date(dateStr).toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch {
        return "—";
    }
}

function PlatformBadge({ type, theme }) {
    const meta = getPlatformMeta(type);
    const isDefault = meta.value === "web";
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 11,
                fontWeight: 700,
                padding: "3px 10px",
                borderRadius: 20,
                letterSpacing: "0.4px",
                textTransform: "uppercase",
                background: isDefault ? theme.primary + "15" : theme.secondary + "18",
                color: isDefault ? theme.primary : theme.secondary,
                border: `1px solid ${isDefault ? theme.primary + "30" : theme.secondary + "30"}`,
            }}
        >
            <i className={`bi ${meta.icon}`} style={{ fontSize: 10 }} />
            {meta.label}
        </span>
    );
}

export default function CredentialsManagerDialog({ open, onClose, application, apiHelper }) {
    const { theme } = useTheme();
    const { showAlertDialog } = useAlertDialog();

    const [view, setView] = useState(VIEWS.LIST);
    const [credentials, setCredentials] = useState(application?.oauth2_clients || []);
    const [listError, setListError] = useState(null);

    // Create form state
    const [newName, setNewName] = useState("");
    const [newPlatform, setNewPlatform] = useState("web");
    const [creating, setCreating] = useState(false);

    // Reveal dialog for a newly generated or regenerated secret
    const [revealOpen, setRevealOpen] = useState(false);
    const [revealKey, setRevealKey] = useState("");
    const [revealSecret, setRevealSecret] = useState("");

    const [deletingId, setDeletingId] = useState(null);
    const [regeneratingId, setRegeneratingId] = useState(null);
    const [loadingCredentials, setLoadingCredentials] = useState(false);

    const fetchCredentials = useCallback(async () => {
        if (!application?.id) return;
        setLoadingCredentials(true);
        try {
            const resp = await apiHelper.apiCall(
                `/arcee/application/oauth-credentials/list?application_id=${application.id}`,
                { method: "GET" }
            );
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.error || "Failed to load credentials.");
            }
            const data = await resp.json();
            setCredentials(data.credentials || []);
        } catch (e) {
            setListError(e.message);
        } finally {
            setLoadingCredentials(false);
        }
    }, [application?.id, apiHelper]);

    useEffect(() => {
        if (open) {
            setView(VIEWS.LIST);
            setNewName("");
            setNewPlatform("web");
            setCredentials([]);
            setListError(null);
            fetchCredentials();
        }
    }, [open, application, fetchCredentials]);

    const handleCreate = async () => {
        if (creating) return;
        setCreating(true);
        try {
            const resp = await apiHelper.apiCall("/arcee/application/oauth-application/create", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    application_id: application.id,
                    name: newName.trim(),
                    platform_type: newPlatform,
                }),
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.error || "Failed to create credentials.");
            }
            const data = await resp.json();
            setRevealKey(data.client_id);
            setRevealSecret(data.client_secret);
            setRevealOpen(true);
            setView(VIEWS.LIST);
            setNewName("");
            setNewPlatform("web");
            await fetchCredentials();
        } catch (e) {
            showAlertDialog({
                title: "Failed to Create Credentials",
                message: e.message,
                type: "error",
                showCancel: false,
            });
        } finally {
            setCreating(false);
        }
    };

    const handleRegenerate = async (credId) => {
        if (regeneratingId) return;
        setRegeneratingId(credId);
        try {
            const resp = await apiHelper.apiCall("/arcee/application/oauth-credentials/regenerate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ credential_id: credId }),
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.error || "Failed to regenerate credentials.");
            }
            const data = await resp.json();
            setRevealKey(data.client_id);
            setRevealSecret(data.client_secret);
            setRevealOpen(true);
            await fetchCredentials();
        } catch (e) {
            alert(e.message);
        } finally {
            setRegeneratingId(null);
        }
    };

    const handleViewKey = (cred) => {
        setRevealKey(cred.client_id);
        setRevealSecret(null);
        setRevealOpen(true);
    };

    const handleDelete = (credId) => {
        showAlertDialog({
            title: "Delete Credential",
            message: "Are you sure you want to delete this credential? All associated access tokens will also be removed. This action cannot be undone.",
            type: "warning",
            okLabel: "Delete",
            cancelLabel: "Cancel",
            showCancel: true,
            onOk: async () => {
                setDeletingId(credId);
                try {
                    const resp = await apiHelper.apiCall("/arcee/application/oauth-credentials/delete", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ credential_id: credId }),
                    });
                    if (!resp.ok) {
                        const err = await resp.json().catch(() => ({}));
                        throw new Error(err.error || "Failed to delete credential.");
                    }
                    await fetchCredentials();
                } catch (e) {
                    showAlertDialog({
                        title: "Delete Failed",
                        message: e.message,
                        type: "error",
                        showCancel: false,
                    });
                } finally {
                    setDeletingId(null);
                }
            },
        });
    };

    if (!open) return null;

    const modalWidth = 640;

    return (
        <>
            {/* Backdrop */}
            <div
                style={{
                    position: "fixed", inset: 0, zIndex: 9998,
                    background: "rgba(30,40,60,0.18)",
                    backdropFilter: "blur(2px)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}
                aria-modal="true" role="dialog"
            >
                <div
                    style={{
                        width: modalWidth,
                        maxWidth: "97vw",
                        maxHeight: "92vh",
                        display: "flex",
                        flexDirection: "column",
                        background: "#fff",
                        borderRadius: theme.cardRadius,
                        boxShadow: "0 12px 48px rgba(0,0,0,.16)",
                        border: "3px solid transparent",
                        backgroundClip: "padding-box, border-box",
                        backgroundImage: `linear-gradient(#fff,#fff), linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
                        backgroundOrigin: "border-box",
                        overflow: "hidden",
                    }}
                >
                    {/* ── Header ── */}
                    <div
                        style={{
                            padding: "20px 24px 14px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderBottom: "1px solid #eaedf3",
                        }}
                    >
                        <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div
                                    style={{
                                        width: 32, height: 32, borderRadius: "50%",
                                        background: theme.primary + "15",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}
                                >
                                    <i className="bi bi-key-fill" style={{ color: theme.primary, fontSize: 15 }} />
                                </div>
                                <span style={{ fontWeight: 700, fontSize: 17, color: theme.primary }}>
                                    Credential Management
                                </span>
                            </div>
                            <p style={{ margin: "4px 0 0 40px", fontSize: 13, color: "#6b7280" }}>
                                {application?.name || "Application"} — OAuth2 credentials
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            style={{
                                border: "none", background: "none",
                                fontSize: 22, color: "#9ca3af", cursor: "pointer",
                                lineHeight: 1, padding: "0 4px",
                            }}
                            title="Close"
                        >
                            &times;
                        </button>
                    </div>

                    {/* ── Body ── */}
                    <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

                        {/* LIST VIEW */}
                        {view === VIEWS.LIST && (
                            <>
                                {loadingCredentials && (
                                    <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
                                        <span className="spinner-border spinner-border-sm" style={{ width: 22, height: 22, borderWidth: 3 }} />
                                    </div>
                                )}

                                {!loadingCredentials && listError && (
                                    <div style={{
                                        background: "#fff1f2", border: "1px solid #fecdd3",
                                        borderRadius: 8, padding: "12px 16px",
                                        color: "#be123c", fontSize: 13,
                                    }}>
                                        <i className="bi bi-exclamation-triangle-fill" style={{ marginRight: 6 }} />
                                        {listError}
                                    </div>
                                )}

                                {!loadingCredentials && !listError && credentials.length === 0 && (
                                    <div style={{
                                        textAlign: "center", padding: "40px 0",
                                        color: "#9ca3af",
                                    }}>
                                        <i className="bi bi-key" style={{ fontSize: 36, display: "block", marginBottom: 10 }} />
                                        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>No credentials yet</div>
                                        <div style={{ fontSize: 13 }}>Click "Add New Credentials" to generate your first one.</div>
                                    </div>
                                )}

                                {!loadingCredentials && credentials.length > 0 && (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                        {credentials.map((cred) => (
                                            <CredentialRow
                                                key={cred.id}
                                                cred={cred}
                                                theme={theme}
                                                onView={() => handleViewKey(cred)}
                                                onRegenerate={() => handleRegenerate(cred.id)}
                                                onDelete={() => handleDelete(cred.id)}
                                                isDeleting={deletingId === cred.id}
                                                isRegenerating={regeneratingId === cred.id}
                                            />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                        {/* CREATE VIEW */}
                        {view === VIEWS.CREATE && (
                            <div>
                                <div
                                    style={{
                                        display: "flex", alignItems: "center", gap: 8,
                                        marginBottom: 20,
                                    }}
                                >
                                    <button
                                        onClick={() => setView(VIEWS.LIST)}
                                        style={{
                                            border: "none", background: "none",
                                            color: theme.primary, cursor: "pointer",
                                            fontSize: 14, padding: "2px 0",
                                            display: "flex", alignItems: "center", gap: 4,
                                        }}
                                    >
                                        <i className="bi bi-arrow-left" /> Back to list
                                    </button>
                                </div>

                                {/* Credential Name */}
                                <div style={{ marginBottom: 18 }}>
                                    <label style={{ fontWeight: 600, fontSize: 14, color: theme.primary, display: "block", marginBottom: 6 }}>
                                        Credential Name <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={newName}
                                        onChange={e => setNewName(e.target.value)}
                                        placeholder="e.g. Production Web App"
                                        style={{
                                            width: "100%",
                                            padding: "9px 12px",
                                            border: `1.5px solid ${theme.primary}40`,
                                            borderRadius: theme.buttonRadius,
                                            fontSize: 14,
                                            color: "#374151",
                                            outline: "none",
                                            background: "#f9fafb",
                                        }}
                                        onFocus={e => e.target.style.borderColor = theme.primary}
                                        onBlur={e => e.target.style.borderColor = theme.primary + "40"}
                                    />
                                </div>

                                {/* Platform Type */}
                                <div style={{ marginBottom: 24 }}>
                                    <label style={{ fontWeight: 600, fontSize: 14, color: theme.primary, display: "block", marginBottom: 8 }}>
                                        Platform Type
                                    </label>
                                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                        {PLATFORM_OPTIONS.map(opt => {
                                            const active = newPlatform === opt.value;
                                            return (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => setNewPlatform(opt.value)}
                                                    style={{
                                                        flex: 1,
                                                        padding: "12px 16px",
                                                        border: `2px solid ${active ? theme.primary : "#e5e7eb"}`,
                                                        borderRadius: theme.buttonRadius,
                                                        background: active ? theme.primary + "10" : "#fff",
                                                        color: active ? theme.primary : "#6b7280",
                                                        fontWeight: active ? 700 : 500,
                                                        fontSize: 14,
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: 8,
                                                        transition: "all .15s",
                                                    }}
                                                >
                                                    <i className={`bi ${opt.icon}`} style={{ fontSize: 16 }} />
                                                    {opt.label}
                                                    {active && (
                                                        <i className="bi bi-check-circle-fill" style={{ fontSize: 13, marginLeft: 2 }} />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Info box */}
                                <div
                                    style={{
                                        background: theme.primary + "08",
                                        border: `1px solid ${theme.primary}20`,
                                        borderRadius: 8,
                                        padding: "10px 14px",
                                        fontSize: 13,
                                        color: "#374151",
                                        marginBottom: 8,
                                    }}
                                >
                                    <i className="bi bi-info-circle-fill" style={{ color: theme.primary, marginRight: 6 }} />
                                    The client secret will be shown <strong>only once</strong>. Copy and store it securely after generation.
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Footer ── */}
                    <div
                        style={{
                            padding: "14px 24px 18px",
                            borderTop: "1px solid #eaedf3",
                            background: "#f8fafc",
                            display: "flex",
                            justifyContent: view === VIEWS.CREATE ? "flex-end" : "space-between",
                            alignItems: "center",
                            gap: 10,
                            borderBottomLeftRadius: theme.cardRadius,
                            borderBottomRightRadius: theme.cardRadius,
                        }}
                    >
                        {view === VIEWS.LIST && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setView(VIEWS.CREATE)}
                                    style={{
                                        background: "#fff",
                                        color: theme.primary,
                                        border: `2px solid ${theme.primary}`,
                                        borderRadius: theme.buttonRadius,
                                        fontWeight: 600,
                                        fontSize: 14,
                                        padding: "8px 20px",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                    }}
                                >
                                    <i className="bi bi-plus-circle" /> Add New Credentials
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    style={{
                                        background: theme.primary,
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: theme.buttonRadius,
                                        fontWeight: 600,
                                        fontSize: 14,
                                        padding: "8px 26px",
                                        cursor: "pointer",
                                    }}
                                >
                                    Close
                                </button>
                            </>
                        )}

                        {view === VIEWS.CREATE && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setView(VIEWS.LIST)}
                                    style={{
                                        background: "#fff",
                                        color: "#6b7280",
                                        border: "1.5px solid #e5e7eb",
                                        borderRadius: theme.buttonRadius,
                                        fontWeight: 600,
                                        fontSize: 14,
                                        padding: "8px 20px",
                                        cursor: "pointer",
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCreate}
                                    disabled={creating}
                                    style={{
                                        background: creating ? theme.primary + "80" : theme.primary,
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: theme.buttonRadius,
                                        fontWeight: 600,
                                        fontSize: 14,
                                        padding: "8px 26px",
                                        cursor: creating ? "not-allowed" : "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                    }}
                                >
                                    {creating
                                        ? <><span className="spinner-border spinner-border-sm" /> Generating…</>
                                        : <><i className="bi bi-lightning-charge-fill" /> Generate Credentials</>
                                    }
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Reveal dialog for newly created / regenerated secret */}
            <ClientKeySecretDialog
                open={revealOpen}
                onClose={() => setRevealOpen(false)}
                clientKey={revealKey}
                clientSecret={revealSecret}
                actionLabel="Done"
            />
        </>
    );
}

/* ── Active/Inactive status badge ── */
function StatusBadge({ isActive }) {
    return (
        <span
            title={isActive ? "This credential is currently in use — a live access token exists" : "No active token — this credential is not currently in use"}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 11,
                fontWeight: 700,
                padding: "3px 9px",
                borderRadius: 20,
                letterSpacing: "0.3px",
                textTransform: "uppercase",
                background: isActive ? "#dcfce7" : "#f3f4f6",
                color: isActive ? "#15803d" : "#6b7280",
                border: `1px solid ${isActive ? "#86efac" : "#e5e7eb"}`,
            }}
        >
            <span
                style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: isActive ? "#16a34a" : "#9ca3af",
                    display: "inline-block",
                    flexShrink: 0,
                }}
            />
            {isActive ? "Active" : "Inactive"}
        </span>
    );
}

/* ── Individual credential row ── */
function CredentialRow({ cred, theme, onView, onRegenerate, onDelete, isDeleting, isRegenerating }) {
    const truncateId = (id) => id?.length > 20 ? id.slice(0, 10) + "…" + id.slice(-8) : id;
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(cred.client_id);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    // has_active_token → credential currently has a live Bearer token in use
    const isInUse = cred.has_active_token === true;

    return (
        <div
            style={{
                border: "1.5px solid #e5e7eb",
                borderRadius: 10,
                padding: "14px 16px",
                background: "#fafbfc",
                transition: "box-shadow .15s",
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,.06)"}
            onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
        >
            {/* Row top: name + platform badge + status badge */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>
                        {cred.name?.trim() || <span style={{ color: "#9ca3af", fontStyle: "italic" }}>Unnamed credential</span>}
                    </span>
                    <PlatformBadge type={cred.platform_type || "web"} theme={theme} />
                    <StatusBadge isActive={isInUse} />
                </div>
                <span style={{ fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap", marginLeft: 8 }}>
                    <i className="bi bi-calendar3" style={{ marginRight: 4 }} />
                    {formatDate(cred.created)}
                </span>
            </div>

            {/* Client ID row */}
            <div
                style={{
                    display: "flex", alignItems: "center", gap: 8,
                    background: "#f3f4f6", borderRadius: 6,
                    padding: "7px 10px",
                    marginBottom: 12,
                }}
            >
                <i className="bi bi-key" style={{ color: theme.primary, fontSize: 13 }} />
                <span style={{ fontFamily: "monospace", fontSize: 13, color: "#374151", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {truncateId(cred.client_id)}
                </span>
                <button
                    onClick={handleCopy}
                    title="Copy Client ID"
                    style={{
                        border: "none", background: "none",
                        color: copied ? "#16a34a" : theme.primary,
                        cursor: "pointer", fontSize: 14, padding: "0 2px",
                    }}
                >
                    <i className={`bi ${copied ? "bi-check-lg" : "bi-clipboard"}`} />
                </button>
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <ActionBtn
                    icon="bi-eye"
                    label="View Key"
                    color={theme.primary}
                    bg={theme.primary + "10"}
                    onClick={onView}
                />
                <ActionBtn
                    icon={isRegenerating ? null : "bi-arrow-repeat"}
                    label={isRegenerating ? "Regenerating…" : "Regenerate"}
                    color={theme.secondary}
                    bg={theme.secondary + "10"}
                    onClick={isRegenerating ? null : onRegenerate}
                    loading={isRegenerating}
                />
                <ActionBtn
                    icon={isDeleting ? null : "bi-trash"}
                    label={isDeleting ? "Deleting…" : "Delete"}
                    color="#dc2626"
                    bg="#fee2e2"
                    onClick={isDeleting ? null : onDelete}
                    loading={isDeleting}
                />
            </div>
        </div>
    );
}

function ActionBtn({ icon, label, color, bg, onClick, loading }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={!onClick}
            style={{
                border: "none",
                background: bg,
                color,
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 12,
                padding: "5px 12px",
                cursor: onClick ? "pointer" : "not-allowed",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                opacity: !onClick ? 0.6 : 1,
                transition: "opacity .15s",
            }}
        >
            {loading
                ? <span className="spinner-border spinner-border-sm" style={{ width: 11, height: 11, borderWidth: 2 }} />
                : icon && <i className={`bi ${icon}`} />
            }
            {label}
        </button>
    );
}
